import { useEffect } from 'react';
import { EpisodeProps, PrevEpisodeProps, Head, PageWrapper, ContactFooter, PageProps, PODCAST_URL } from '@acueducto/shared';
import { loadEpisodes, episodeListItem } from '../utils/prismicEpisodes.cjs';
import type { GetStaticPropsContext } from 'next';
import { createClient } from '../prismicio';
import { PodcastEpisodePage } from '../components/pages/podcast-episode/PodcastEpisodePage';

interface EpisodioProps extends PageProps {
  episode: EpisodeProps;
  prevEpisode: PrevEpisodeProps | null;
}

/**
 * Componente principal para la página de un episodio de podcast
 * Lee todos los episodios desde Prismic CMS
 */
export default function Episodio({
  setTitle,
  episode,
  prevEpisode,
}: EpisodioProps) {
  useEffect(() => {
    setTitle('Podcast');
  }, [setTitle]);

  const titleContent = episode.seo_title
    ? episode.seo_title
    : `${episode.title} | ${episode.guest}, ${episode.business}`;

  const imageAlt = `${episode.title} | ${episode.guest}, ${episode.business}`;

  const imageFileName = episode.gif;

  return (
    <PageWrapper>
      <Head
        title={titleContent}
        description={episode.description}
        headerTitle="Episodio"
        es_canonical={`${PODCAST_URL}/${episode.slug}`}
        siteRoot={PODCAST_URL}
        image={{
          fileName: imageFileName ?? '',
          alt: imageAlt,
        }}
        podcastEpisode={{
          episodeNumber: episode.episodeNumber,
          datePublished: episode.date,
          guest: episode.guest,
          business: episode.business,
          spotify: episode.spotify,
          apple: episode.apple,
          youtube: episode.youtube,
        }}
      />
      <PodcastEpisodePage episode={episode} prevEpisode={prevEpisode} />
      <ContactFooter />
    </PageWrapper>
  );
}

export async function getStaticProps({ params, locale }: GetStaticPropsContext) {
  if (locale !== 'es') return { notFound: true };
  // Errors propagate: ISR keeps the previous good page instead of caching a false 404.
  const episodes = await loadEpisodes(createClient());
  const episode = episodes.find((item) => item.slug === params?.slug);
  if (!episode) return { notFound: true };
  const prevEpisode = episodes.find((item) => item.episodeNumber < episode.episodeNumber) ?? null;
  return {
    props: {
      episode,
      prevEpisode: prevEpisode ? episodeListItem(prevEpisode) : null,
    },
    revalidate: 3600,
  };
}

export async function getStaticPaths() {
  const episodes = await loadEpisodes(createClient());
  return {
    paths: episodes.map(({ slug }) => ({ params: { slug } })),
    fallback: 'blocking',
  };
}
